/**
 * Backlog projection — where each unplanned request lands.
 *
 * Spec v0.5 §5.3 decision 4: this is one pure function, run on the server for
 * truth and in the browser for instant drag feedback. It must therefore stay
 * free of clocks, randomness and I/O.
 *
 * It deliberately departs from the prototype (`Rundown.dc.html:958-962`), whose
 * greedy first-fit has four defects that must not carry over:
 *
 *   1. Locked sprints still received projections — the loop never checked the
 *      lock, so after Start sprint new backlog items projected *into* it,
 *      contradicting §6.4 rule 2. Locked sprints are excluded here.
 *   2. First-fit backfill contradicted "rank decides order": rescanning from
 *      sprint 1 every time let a low-ranked small item land ahead of a
 *      high-ranked large one. Strict rank order is the default; backfill is an
 *      explicit opt-in and every backfilled landing is labelled as such.
 *   3. `ownSprint` starved: it required a completely empty sprint, so any
 *      earlier item landing in one denied it permanently. Exclusive sprints are
 *      now reserved in a pass *before* the greedy one.
 *   4. Carry-over was ignored, so every projection turned optimistic the moment
 *      a sprint ran over. Unfinished locked work now consumes capacity.
 *
 * And one the prototype never met because its sample data was all estimated:
 *
 *   5. An unestimated (zero-hour) item fits every sprint, so an imported
 *      backlog of them all landed in the first open one. They now land in
 *      LATER with reason 'unestimated' unless someone pins them.
 *
 * Capacity is global on the projection but per-person on the Planning screen.
 * Both come from this one call: loads are always accumulated per sprint *and*
 * per owner, and `capacityMode` only decides which of the two constrains
 * placement. That is what keeps the two screens from drifting apart.
 */
/** Items that do not fit any open sprint land here. */
export const LATER = 'later';
export function project(input) {
    const capacityMode = input.capacityMode ?? 'team';
    const fill = input.fill ?? 'strict-rank';
    const plannableHours = input.plannableHours;
    if (!Number.isFinite(plannableHours) || plannableHours < 0) {
        throw new RangeError(`plannableHours must be a non-negative number, got ${plannableHours}`);
    }
    // Correction 1: a locked sprint is not a candidate for anything.
    const openSprints = input.sprints.filter((sprint) => !sprint.locked);
    const keys = [...openSprints.map((sprint) => sprint.key), LATER];
    const load = {};
    const loadByOwner = {};
    const unattributedLoad = {};
    for (const key of keys) {
        load[key] = 0;
        loadByOwner[key] = {};
        unattributedLoad[key] = 0;
    }
    const addLoad = (key, ownerId, hours) => {
        load[key] = (load[key] ?? 0) + hours;
        if (ownerId) {
            const owners = loadByOwner[key];
            owners[ownerId] = (owners[ownerId] ?? 0) + hours;
        }
        else {
            unattributedLoad[key] = (unattributedLoad[key] ?? 0) + hours;
        }
    };
    const firstOpenKey = openSprints[0]?.key;
    // Correction 4: unfinished locked work consumes the next open sprint.
    if (firstOpenKey !== undefined) {
        const carryOver = input.sprints
            .filter((sprint) => sprint.locked)
            .reduce((total, sprint) => total + (sprint.unfinishedHours ?? 0), 0);
        if (carryOver > 0)
            addLoad(firstOpenKey, null, carryOver);
    }
    // Work placed by hand rather than projected.
    for (const sprint of openSprints) {
        if (sprint.committedHours)
            addLoad(sprint.key, null, sprint.committedHours);
    }
    const openKeySet = new Set(openSprints.map((sprint) => sprint.key));
    /** Sprints claimed by an `ownSprint` item. Nothing else may land in them. */
    const exclusive = new Set();
    const landings = [];
    const landing = {};
    const place = (item, key, reason, backfilled = false) => {
        landing[item.id] = key;
        landings.push({ itemId: item.id, sprint: key, reason, backfilled });
        if (key !== LATER)
            addLoad(key, item.ownerId, item.hours);
    };
    // --- Pass 1: pinned items. A pin is a human decision, so it is honoured even
    // when it overloads the sprint; the load bar is what reports that.
    const unpinned = [];
    for (const item of input.items) {
        const pinned = item.pinnedSprint;
        if (pinned && openKeySet.has(pinned)) {
            place(item, pinned, 'pinned');
            if (item.ownSprint)
                exclusive.add(pinned);
        }
        else {
            unpinned.push(item);
        }
    }
    // --- Pass 2 (correction 3): reserve a sprint for each exclusive item before
    // the greedy pass runs, so an ordinary item cannot starve it.
    const remaining = [];
    for (const item of unpinned) {
        // Correction 5: an item nobody has estimated weighs nothing, so without
        // this every one of them "fits" the first open sprint and the plan shows a
        // sprint full of work its load bar says is empty. It needs analysis before
        // it can be said to land anywhere. A pin (pass 1) still places it: that is
        // a person deciding, not arithmetic.
        if (!(item.hours > 0)) {
            place(item, LATER, 'unestimated');
            continue;
        }
        if (!item.ownSprint) {
            remaining.push(item);
            continue;
        }
        const free = openSprints.find((sprint) => !exclusive.has(sprint.key) && (load[sprint.key] ?? 0) === 0);
        if (free) {
            exclusive.add(free.key);
            place(item, free.key, 'exclusive');
        }
        else {
            place(item, LATER, 'overflow');
        }
    }
    // --- Pass 3: everything else, in rank order.
    const fits = (key, item) => {
        if (exclusive.has(key))
            return false;
        if (capacityMode === 'per-owner' && item.ownerId) {
            const ownerCap = input.ownerPlannableHours?.[item.ownerId] ?? plannableHours;
            const ownerLoad = loadByOwner[key]?.[item.ownerId] ?? 0;
            return ownerLoad + item.hours <= ownerCap;
        }
        return (load[key] ?? 0) + item.hours <= plannableHours;
    };
    // The frontier is what makes rank order mean something: it never moves back,
    // so a lower-ranked item can never land earlier than a higher-ranked one.
    let frontier = 0;
    let overflowed = false;
    for (const item of remaining) {
        if (openSprints.length === 0) {
            place(item, LATER, 'no-open-sprint');
            continue;
        }
        // Once one item overflows, strict rank order requires every later item to
        // follow it. Backfill is the mode that deliberately relaxes this.
        if (overflowed && fill === 'strict-rank') {
            place(item, LATER, 'overflow');
            continue;
        }
        const searchFrom = fill === 'backfill' ? 0 : frontier;
        let placedAt = -1;
        for (let index = searchFrom; index < openSprints.length; index += 1) {
            if (fits(openSprints[index].key, item)) {
                placedAt = index;
                break;
            }
        }
        if (placedAt < 0) {
            overflowed = true;
            place(item, LATER, 'overflow');
            continue;
        }
        const backfilled = placedAt < frontier;
        place(item, openSprints[placedAt].key, backfilled ? 'backfilled' : 'rank', backfilled);
        if (placedAt > frontier)
            frontier = placedAt;
    }
    return {
        keys,
        landings,
        landing,
        load,
        loadByOwner,
        unattributedLoad,
        plannableHours,
        capacityMode,
        fill,
    };
}
//# sourceMappingURL=projection.js.map